import { getData, TableDataResponse } from '../get-table'
import { HttpService } from '../http.service'

// Ensure Omit is available (for older TypeScript versions)
type Omit<T, K extends keyof any> = Pick<T, Exclude<keyof T, K>>

const http = new HttpService()

export type AUTOMATIC_EVENT_TYPE =
  | 'SCHEDULED'
  | 'EMPLOYEE_CREATED'
  | 'EMPLOYEE_SALARY_CREATED'
  | 'EMPLOYEE_DEPARTMENT_UPDATED'
  | 'EMPLOYEE_POSITION_UPDATED'
  | 'EMPLOYEE_SUCURSAL_UPDATED'

export const AUTOMATIC_EVENT_TYPES_LABELS = (key: AUTOMATIC_EVENT_TYPE) => {
  return {
    ['SCHEDULED']: 'Programado',
    ['EMPLOYEE_CREATED']: 'Empleado creado',
    ['EMPLOYEE_SALARY_CREATED']: 'Salario creado',
    ['EMPLOYEE_DEPARTMENT_UPDATED']: 'Departamento actualizado',
    ['EMPLOYEE_POSITION_UPDATED']: 'Cargo actualizado',
    ['EMPLOYEE_SUCURSAL_UPDATED']: 'Sucursal actualizada'
  }[key]
}

export enum SCHEDULED_FREQUENCIES {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  FORTNIGHTLY = 'FORTNIGHTLY',
  MONTHLY = 'MONTHLY',
  BIMONTHLY = 'BIMONTHLY'
}

export const SCHEDULED_FREQUENCIES_LABELS: Record<
  SCHEDULED_FREQUENCIES,
  string
> = {
  [SCHEDULED_FREQUENCIES.DAILY]: 'Diario',
  [SCHEDULED_FREQUENCIES.WEEKLY]: 'Semanal',
  [SCHEDULED_FREQUENCIES.FORTNIGHTLY]: 'Quincenal',
  [SCHEDULED_FREQUENCIES.MONTHLY]: 'Mensual',
  [SCHEDULED_FREQUENCIES.BIMONTHLY]: 'Bimestral'
}

export enum SCHEDULED_FREQUENCIES_OPTIONS {
  FIRST_DAY = 'FIRST_DAY',
  LAST_DAY = 'LAST_DAY'
}

export const SCHEDULED_FREQUENCIES_OPTIONS_LABELS: Record<
  SCHEDULED_FREQUENCIES_OPTIONS,
  string
> = {
  [SCHEDULED_FREQUENCIES_OPTIONS.FIRST_DAY]: 'Primer día',
  [SCHEDULED_FREQUENCIES_OPTIONS.LAST_DAY]: 'Último día'
}

export interface Process {
  id: string
  name: string
  code: string
  color: string
  countries: string[]
  showInCalc: boolean
  automatic: string[]
  lastUpdate: string
}

export async function getProcesses(
  offset: number,
  perPage: number,
  dataOrder: string,
  search: string = ''
): Promise<TableDataResponse<Process>> {
  // where's
  const where = []
  if (search) {
    where.push(`CONCAT(processes.code,processes.name) ILIKE '%${search}%'`)
  }
  // order by
  let modifier = [
    'GROUP BY processes.id, processes.code, processes.name, processes.apply_calc, processes.automatic_events, processes.updated_at',
    dataOrder === ''
      ? 'ORDER BY processes.name'
      : `ORDER BY ${dataOrder[1]} ${dataOrder[0]}`
  ]

  const result = await getData<[TableDataResponse<any>]>({
    tables: ['pg_processes'],
    fields: [
      'processes.id',
      'processes.code',
      'processes.name',
      'processes.color',
      'processes.apply_calc as showInCalc',
      'processes.automatic_events as automatic',
      'processes.updated_at as lastUpdate',
      'ARRAY_AGG(c.name ORDER BY c.name) as countries'
    ],
    join: [
      'LEFT JOIN LATERAL jsonb_array_elements_text(processes.countries) country_id(id) ON TRUE',
      'LEFT JOIN countries c ON c.id::text = country_id.id'
    ],
    where,
    modifier,
    options: {
      outputFormat: 'json',
      pagination: { offset, limit: perPage }
    }
  })
  return {
    results: result[0].results.map(process => ({
      ...process,
      automatic: process.automatic?.map(
        (event: { type: AUTOMATIC_EVENT_TYPE }) =>
          AUTOMATIC_EVENT_TYPES_LABELS(event.type)
      )
    })),
    totalItems: result[0].totalItems
  }
}

export async function getCountries(): Promise<
  {
    id: string
    name: string
  }[]
> {
  const result = await getData<[TableDataResponse<any>]>({
    tables: ['pg_countries'],
    fields: ['id', 'code', 'name'],
    options: {
      outputFormat: 'json'
    },
    modifier: ['ORDER BY code']
  })

  return result[0]?.results.map(country => ({
    id: country.id,
    name: `${country.code} - ${country.name}`
  }))
}

export async function getProcessById(id: string): Promise<Process> {
  const result = await getData<[TableDataResponse<any>]>({
    tables: ['pg_processes'],
    fields: ['*'],
    where: [`processes.id='${id}'`],

    options: {
      outputFormat: 'json'
    }
  })
  return {
    id: result[0].results[0].id,
    code: result[0].results[0].code,
    name: result[0].results[0].name,
    color: result[0].results[0].color,
    countries: result[0].results[0].countries,
    showInCalc: result[0].results[0].apply_calc,
    automatic: result[0].results[0].automatic_events,
    lastUpdate: result[0].results[0].lastUpdate
  }
}

interface ProcessForUpdate extends Omit<Process, 'lastUpdate' | 'id'> {
  automatic_events: []
}

export async function updateProcess(
  process: ProcessForUpdate,
  id: string
): Promise<void> {
  console.log('Updating process:', process)
  await http
    .put(`/concept/processes/${id}`, {
      code: process.code,
      name: process.name,
      color: process.color,
      countries: process.countries,
      applyCalc: process.showInCalc,
      automaticEvents: process.automatic_events
    })
    .catch(err => {
      console.error('Error updating process:', err)
      throw new Error('Error updating process')
    })
}
